import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import UpArrowIcon from "../icon/up-arrow.svg";
import StopIcon from "../icon/stop.svg";
import { useAppConfig } from "../context/AppConfigContext";
import { getChatStarters, getMybroWarningWording } from "../utils/api";
import { useFileUpload } from "../hooks/useFileUpload";

/** 後端無資料或請求失敗時之後援，須與營運預設一致 */
const DEFAULT_CHAT_DISCLAIMER =
  "MyBro 助理小哥是 AI，有時可能會出錯。 | v2.0.0 (BETA)";

/** 依 MIME type 回傳代表性 emoji，用於非圖片 chip 的視覺提示 */
const getFileTypeIcon = (mimeType) => {
  if (mimeType === "application/pdf") return "📄";
  if (mimeType.includes("word") || mimeType === "application/msword") return "📝";
  if (mimeType.includes("excel") || mimeType.includes("spreadsheet") || mimeType === "text/csv") return "📊";
  if (mimeType.includes("powerpoint") || mimeType.includes("presentation")) return "📈";
  if (
    mimeType.startsWith("text/") ||
    mimeType === "application/json" ||
    mimeType.includes("xml") ||
    mimeType.includes("yaml")
  ) return "📃";
  return "📎";
};

// 允許的副檔名白名單 — accept 屬性與前端驗證共用同一份，新增格式只需改這裡
const ALLOWED_EXTENSIONS = new Set([
  ".pdf", ".docx", ".xlsx", ".csv", ".pptx",
  ".txt", ".md", ".json", ".xml", ".yaml", ".yml", ".log", ".py",
]);

/** 取得小寫副檔名（含點），找不到回傳空字串 */
const getExtension = (filename) => {
  const m = filename.toLowerCase().match(/(\.[^.]+)$/);
  return m ? m[1] : "";
};

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB
const MAX_FILE_COUNT = 6;

const buildUploadErrorText = (errorEntries) => {
  if (!errorEntries?.length) return "";
  const firstMessage = errorEntries[0]?.errorMessage;
  if (errorEntries.length === 1) {
    return firstMessage || "檔案上傳失敗，請移除後重新上傳";
  }
  return `${errorEntries.length} 個檔案上傳失敗，請移除失敗檔案後重新上傳`;
};

export default function ChatRichTextarea({
  input,
  setInput,
  onSendMessage,
  onStopGeneration,
  isStopping,
  isLoading,
  // 這個對話還有未解決的提問在等回覆（含正常送出中、補收確認 processing、補收冷卻中尚未確定放棄）：
  // 與 isLoading 同樣鎖住本對話輸入，避免對同一 GenAIus session 再問第二題（該服務同一 session
  // 一次只處理一題，併發第二題只會拿到空回覆）。與 isLoading 分開的原因：此鎖只綁「這個對話」，
  // 切換／開新對話時為 false，輸入照常可用。
  isAwaitingReply = false,
  chatId,
  onEnsureChatSession,
  isNewChat = false,
  isMobile = false,
  newChatStartersMountEl = null,
}) {
  const { fileUploadEnabled } = useAppConfig();
  // 輸入鎖：AI 回覆中（isLoading）或還有提問等回覆中（isAwaitingReply）皆鎖住本對話輸入，行為一致。
  const inputLocked = isLoading || isAwaitingReply;
  const [starters, setStarters] = useState([]);
  const [loadingStarters, setLoadingStarters] = useState(false);
  const [chatDisclaimerText, setChatDisclaimerText] = useState(
    DEFAULT_CHAT_DISCLAIMER
  );

  const wrapperRef = useRef(null);
  const rtRef = useRef(null);
  const isComposingRef = useRef(false);
  const prevInputLockedRef = useRef(inputLocked);
  const fileInputRef = useRef(null);
  const focusListenerRef = useRef(null);
  // 快取第一次 ensureChatSession 解析出的真實 session id，供同一問答週期內的
  // 後續上傳（刪除後重選）及 removeEntry 使用，避免重複建立 backend session
  const resolvedChatIdRef = useRef(null);

  // 前端驗證錯誤訊息
  const [fileError, setFileError] = useState("");
  // 使用者已點擊附件按鈕、OS 檔案選取對話框尚未關閉期間為 true
  const [isSelectingFile, setIsSelectingFile] = useState(false);
  // 上傳前警語 modal
  const [showUploadWarning, setShowUploadWarning] = useState(false);
  // drag/drop 懸停狀態
  const [dragActive, setDragActive] = useState(false);

  const fileUpload = useFileUpload({ maxTotal: MAX_FILE_COUNT });

  // picker 關閉偵測用的 listener 清除（focus = 桌機、visibilitychange = 手機）
  const removePickerListeners = () => {
    if (!focusListenerRef.current) return;
    const { onFocus, onVisibility } = focusListenerRef.current;
    window.removeEventListener('focus', onFocus);
    document.removeEventListener('visibilitychange', onVisibility);
    focusListenerRef.current = null;
  };

  // unmount 時移除 picker listeners（useFileUpload 自己負責 cleanup blob URLs）
  useEffect(() => {
    return () => removePickerListeners();
  }, []);

  useEffect(() => {
    if (!isNewChat) {
      setStarters([]);
      setLoadingStarters(false);
      return;
    }
    let cancelled = false;
    setLoadingStarters(true);
    getChatStarters()
      .then((list) => {
        if (!cancelled) {
          setStarters(Array.isArray(list) ? list : []);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingStarters(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isNewChat]);

  useEffect(() => {
    let cancelled = false;
    void getMybroWarningWording().then((text) => {
      if (cancelled || text == null || text === "") return;
      setChatDisclaimerText(text);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // 自動調整高度（換行時增高），最大 160px
  const adjustHeight = () => {
    const textarea = rtRef.current;
    if (!textarea) return;
    const minHeight = 48;
    const maxHeight = 160;
    textarea.style.height = "1px";
    const nextHeight = Math.min(maxHeight, Math.max(minHeight, textarea.scrollHeight));
    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? "auto" : "hidden";
  };

  useEffect(() => {
    adjustHeight();
  }, [input]);

  // 輸入解鎖後自動聚焦：涵蓋 AI 回覆完成（isLoading 由 true→false）與等待回覆結束（isAwaitingReply 由 true→false）。
  // 手機版不重新聚焦，避免虛擬鍵盤跳出蓋住剛送達的回覆內容。
  useEffect(() => {
    if (prevInputLockedRef.current === true && inputLocked === false && !isMobile) {
      setTimeout(() => {
        if (rtRef.current) rtRef.current.focus();
      }, 100);
    }
    prevInputLockedRef.current = inputLocked;
  }, [inputLocked, isMobile]);

  // 重複檔案通知：6 秒後自動清除
  useEffect(() => {
    if (!fileUpload.duplicateNotice) return;
    const timer = setTimeout(() => fileUpload.clearDuplicateNotice(), 6000);
    return () => clearTimeout(timer);
  }, [fileUpload.duplicateNotice]);

  // chatId 任何變動（切換對話、temp 升級為真實 id）時清除快取的 session id
  useEffect(() => {
    resolvedChatIdRef.current = null;
  }, [chatId]);

  const handleKeyDown = (e) => {
    // 組字（IME）確認候選字的 Enter 不代表要送出訊息，Mac Safari/Chrome 會回報 key === "Enter" 且 isComposing === true
    if (e.nativeEvent?.isComposing || e.isComposing || isComposingRef.current || e.keyCode === 229) return;
    if (e.key !== "Enter") return;
    if (e.shiftKey) {
      e.preventDefault();
      const ta = rtRef.current;
      if (!ta) return;
      const start = ta.selectionStart ?? input.length;
      const end = ta.selectionEnd ?? input.length;
      const next = input.slice(0, start) + "\n" + input.slice(end);
      setInput(next);
      const cursor = start + 1;
      requestAnimationFrame(() => {
        if (rtRef.current) {
          rtRef.current.selectionStart = rtRef.current.selectionEnd = cursor;
        }
      });
      return;
    }
    if (!inputLocked && (input.trim() || fileUpload.totalCount > 0)) {
      e.preventDefault();
      handleSend();
    }
  };

  // 點擊附件按鈕：先顯示警語 modal，等使用者確認後再開 file picker
  const handleAttachClick = () => {
    if (fileUpload.totalCount >= MAX_FILE_COUNT) {
      setFileError(`最多可上傳 ${MAX_FILE_COUNT} 個檔案`);
      return;
    }
    setFileError("");
    setShowUploadWarning(true);
  };

  const handleWarningCancel = () => {
    setShowUploadWarning(false);
  };

  // 警語 modal「確認」— 必須直接在 onClick 裡呼叫 fileInput.click()，
  // 才能滿足 iOS Safari / Android Chrome 的 user gesture 限制
  const handleWarningConfirm = () => {
    setShowUploadWarning(false);
    setIsSelectingFile(true);
    removePickerListeners();

    let settled = false;
    const onClose = () => {
      if (settled) return;
      settled = true;
      removePickerListeners();
      setTimeout(() => setIsSelectingFile(false), 300);
    };

    const onFocus = onClose;
    const onVisibility = () => { if (!document.hidden) onClose(); };

    focusListenerRef.current = { onFocus, onVisibility };
    window.addEventListener('focus', onFocus, { once: true });
    document.addEventListener('visibilitychange', onVisibility);

    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileSelectCancel = () => {
    removePickerListeners();
    setIsSelectingFile(false);
  };

  // 共用驗證與上傳邏輯：數量上限檢查、ensureChatSession、addFiles。
  // click / drag-drop / paste 三條路徑均呼叫此函式。
  const validateAndQueueFiles = useCallback(async (files) => {
    if (!files.length) return;

    setFileError("");
    const valid = [];
    let hitCountLimit = false;

    for (const file of files) {
      if (fileUpload.totalCount + valid.length >= MAX_FILE_COUNT) {
        hitCountLimit = true;
        break;
      }
      valid.push(file);
    }

    if (hitCountLimit) setFileError(`最多可上傳 ${MAX_FILE_COUNT} 個檔案`);

    if (valid.length > 0) {
      // eager upload 需要真實 session_id。
      // 優先使用本週期已解析的 resolvedChatIdRef（刪除後重選時不重複建立 session）；
      // 若尚無快取且 chatId 仍是 temp，才呼叫 ensureChatSession 並將結果存入 ref。
      let effectiveChatId = resolvedChatIdRef.current || chatId;
      if (onEnsureChatSession && (!effectiveChatId || String(effectiveChatId).startsWith('temp-'))) {
        try {
          effectiveChatId = await onEnsureChatSession();
          resolvedChatIdRef.current = effectiveChatId;
        } catch {
          setFileError('建立對話失敗，請重試');
          return;
        }
      }
      fileUpload.addFiles(valid, effectiveChatId);
    }
  }, [fileUpload, chatId, onEnsureChatSession]);

  // --- Drag/Drop handlers ---

  const handleDragEnter = (e) => {
    if (!fileUploadEnabled) return;
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragOver = (e) => {
    if (!fileUploadEnabled) return;
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    if (!fileUploadEnabled) return;
    // 只有真正離開 wrapper 時才取消高亮，避免子元素觸發 flicker
    if (wrapperRef.current && !wrapperRef.current.contains(e.relatedTarget)) {
      setDragActive(false);
    }
  };

  const handleDrop = async (e) => {
    if (!fileUploadEnabled) return;
    e.preventDefault();
    setDragActive(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (!files.length) return;
    await validateAndQueueFiles(files);
  };

  // --- Paste handler ---

  const handlePaste = async (e) => {
    if (!fileUploadEnabled) return;
    const files = Array.from(e.clipboardData?.files || []);
    if (!files.length) return; // 純文字貼上，維持預設行為
    e.preventDefault();
    await validateAndQueueFiles(files);
  };

  // 處理 file picker 回傳的檔案（點擊 + 按鈕路徑）
  const handleFilesSelected = useCallback(async (e) => {
    removePickerListeners();
    const selected = Array.from(e.target.files || []);
    e.target.value = "";
    if (!selected.length) {
      setIsSelectingFile(false);
      return;
    }
    await validateAndQueueFiles(selected);
    // picker spinner 結束（縮圖生成在 hook 內部非同步進行）
    setIsSelectingFile(false);
  }, [validateAndQueueFiles]);

  // 發送：有附件但無文字時顯示提示；上傳進行中阻擋送出
  const handleSend = () => {
    if (fileUpload.isUploading) {
      setFileError("⏳ 請等待檔案上傳完成後再送出");
      return;
    }
    if (fileUpload.hasError) {
      setFileError(buildUploadErrorText(fileUpload.errorEntries));
      return;
    }
    if (fileUpload.totalCount > 0 && !input.trim()) {
      setFileError("請輸入問題後再送出附件");
      return;
    }
    setFileError("");
    // 手機版送出前先收鍵盤：避免送出後畫面捲到使用者訊息置頂時，鍵盤仍佔用可視高度導致位置算錯
    if (isMobile) {
      rtRef.current?.blur();
    }
    if (fileUpload.totalCount > 0) {
      // fileData 保持 file 與 serverPath 的對應，避免成功/失敗混合時 index 錯位
      const fileData = fileUpload.entries.map((e) => ({
        file: e.file,
        serverPath: e.uploadStatus === 'done' ? e.serverPath : null,
      }));
      onSendMessage({ fileData });
      fileUpload.clearAll();
      resolvedChatIdRef.current = null; // 問答週期結束，重置快取的 session id
    } else {
      onSendMessage();
    }
  };

  const hasFiles = fileUpload.totalCount > 0;
  const uploadErrorText = buildUploadErrorText(fileUpload.errorEntries);
  const displayFileError = fileError || uploadErrorText || fileUpload.removeNotice || fileUpload.duplicateNotice;
  const canSend = (input.trim().length > 0 || hasFiles) && !inputLocked && !fileUpload.isUploading && !fileUpload.hasError;
  const showButton = canSend || isLoading;
  const showStarterPills =
    isNewChat && !loadingStarters && starters.length > 0;

  const handleStarterActivate = (label) => {
    if (inputLocked || !label) return;
    onSendMessage(label);
  };

  const handleRemoveFileEntry = (id) => {
    // 使用解析後的真實 session id；若尚未解析（不應發生）則 fallback 至 prop chatId
    fileUpload.removeEntry(id, resolvedChatIdRef.current || chatId);
    setFileError("");
    fileUpload.clearRemoveNotice();
  };

  const starterPillsNode = showStarterPills ? (
    <div
      className="chat-starter-pills-region"
      role="region"
      aria-label="常用開頭語"
    >
      <div
        className={`chat-starter-pills ${isMobile ? "chat-starter-pills-mobile" : ""}`}
      >
        {starters.map((item, idx) => (
          <button
            key={item.seqNo != null ? String(item.seqNo) : `starter-${idx}`}
            type="button"
            className="chat-starter-pill"
            onClick={() => handleStarterActivate(item.starterLabel)}
          >
            {item.starterLabel}
          </button>
        ))}
      </div>
    </div>
  ) : null;

  return (
    <>
    {/* portal 到 body：避免被輸入區所在的 stacking context 限制，導致 sidebar 疊在遮罩上方仍可點擊（AGUI-1249） */}
    {showUploadWarning && createPortal(
      <div
        className="upload-warning-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="upload-warning-title"
        // 點遮罩空白處視同取消；點 modal 內部（target 非遮罩本身）不觸發
        onClick={(e) => { if (e.target === e.currentTarget) handleWarningCancel(); }}
      >
        <div className="upload-warning-modal">
          <p id="upload-warning-title" className="upload-warning-text">
            {`✨ 檔案上傳 Beta 版\n📏 限制： 最多 6 個檔案, 單檔小於5MB。\n若上傳或解析失敗請見諒, 下一版會更強大~`}
          </p>
          <div className="upload-warning-actions">
            <button className="upload-warning-btn upload-warning-btn--cancel" onClick={handleWarningCancel}>
              取消
            </button>
            <button className="upload-warning-btn upload-warning-btn--confirm" onClick={handleWarningConfirm}>
              確認上傳
            </button>
          </div>
        </div>
      </div>,
      document.body
    )}
    <div className={`input-area ${isNewChat && !isMobile ? "input-area-centered" : ""}`}>
      <div
        className={`input-wrapper${dragActive ? " input-wrapper--drag-over" : ""}`}
        ref={wrapperRef}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >

        {/* 已選取的檔案 chip 列表 */}
        {hasFiles && (
          <div className="attachment-chips">
            {fileUpload.entries.map((entry) => (
              entry.isImage ? (
                <div
                  key={entry.id}
                  className={`attachment-chip attachment-chip--image${entry.uploadStatus === 'uploading' ? ' attachment-chip--uploading' : ''}${entry.uploadStatus === 'error' ? ' attachment-chip--error' : ''}`}
                  title={entry.file.name}
                >
                  {entry.previewUrl
                    ? <img src={entry.previewUrl} alt={entry.file.name} className="attachment-chip-thumb" />
                    : <div className="attachment-chip-thumb-placeholder" />
                  }
                  {entry.uploadStatus === 'uploading' && (
                    <span className="chip-upload-spinner" aria-hidden="true" />
                  )}
                  {entry.uploadStatus === 'error' && (
                    <span className="chip-error-icon" aria-hidden="true" title={entry.errorMessage}>⚠</span>
                  )}
                  <button
                    className="attachment-chip-remove"
                    onClick={() => handleRemoveFileEntry(entry.id)}
                    disabled={entry.uploadStatus === 'uploading'}
                    title={entry.uploadStatus === 'uploading' ? '上傳中，請稍候' : '移除'}
                    aria-label={`移除 ${entry.file.name}`}
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <div
                  key={entry.id}
                  className={`attachment-chip${entry.uploadStatus === 'uploading' ? ' attachment-chip--uploading' : ''}${entry.uploadStatus === 'error' ? ' attachment-chip--error' : ''}`}
                  title={entry.errorMessage || entry.file.name}
                >
                  <span className="attachment-chip-icon" aria-hidden="true">
                    {entry.uploadStatus === 'uploading'
                      ? <span className="chip-upload-spinner chip-upload-spinner--sm" aria-hidden="true" />
                      : entry.uploadStatus === 'error'
                      ? '⚠'
                      : getFileTypeIcon(entry.file.type)
                    }
                  </span>
                  <span className="attachment-chip-name">{entry.file.name}</span>
                  <button
                    className="attachment-chip-remove"
                    onClick={() => handleRemoveFileEntry(entry.id)}
                    disabled={entry.uploadStatus === 'uploading'}
                    title={entry.uploadStatus === 'uploading' ? '上傳中，請稍候' : '移除'}
                    aria-label={`移除 ${entry.file.name}`}
                  >
                    ✕
                  </button>
                </div>
              )
            ))}
          </div>
        )}

        {/* 前端驗證錯誤 / 上傳狀態提示 */}
        {displayFileError && (
          <div className="attachment-error">{displayFileError}</div>
        )}

        <div className="textarea-with-button">
          {/* 附件按鈕（僅在 fileUploadEnabled 時顯示） */}
          {fileUploadEnabled && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: "none" }}
                onChange={handleFilesSelected}
                onCancel={handleFileSelectCancel}
                accept={`image/*,${[...ALLOWED_EXTENSIONS].join(",")}`}
              />
              <button
                className={`attach-button${isSelectingFile ? " attach-button--selecting" : ""}`}
                onClick={handleAttachClick}
                disabled={inputLocked || isSelectingFile}
                title="新增檔案"
                aria-label={isSelectingFile ? "選取中..." : "新增檔案"}
                draggable={false}
              >
                {isSelectingFile ? (
                  <span className="attach-spinner" aria-hidden="true" />
                ) : (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M8 2v12M2 8h12" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
                  </svg>
                )}
              </button>
            </>
          )}

          <div className="chat-textarea-wrapper">
            <textarea
              id="chat-input"
              name="chat-input"
              ref={rtRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              onCompositionStart={() => { isComposingRef.current = true; }}
              onCompositionEnd={() => { isComposingRef.current = false; }}
              onPaste={handlePaste}
              placeholder="問問 MyBro 助理小哥"
              className={`chat-textarea ${showButton ? "has-button" : ""}`}
              onInput={adjustHeight}
              disabled={inputLocked}
              rows={1}
            />
          </div>

          {showButton && (
            isLoading ? (
              <button
                className={`send-button-inside stop-button${isStopping ? ' is-stopping' : ''}`}
                onClick={onStopGeneration}
                disabled={isStopping}
                title={isStopping ? '停止中…' : '停止 AI 回覆'}
                aria-label={isStopping ? '停止中' : '停止 AI 回覆'}
              >
                <img src={StopIcon} alt="停止" />
              </button>
            ) : (
              <button
                className="send-button-inside"
                onClick={handleSend}
                disabled={!canSend}
                title="提交"
              >
                <img src={UpArrowIcon} alt="提交" />
              </button>
            )
          )}
        </div>

        {starterPillsNode &&
          (isMobile && newChatStartersMountEl
            ? createPortal(starterPillsNode, newChatStartersMountEl)
            : !isMobile && starterPillsNode)}

        <div className="chat-input-disclaimer chat-input-disclaimer--below">
          {chatDisclaimerText}
        </div>
      </div>
    </div>
    </>
  );
}
